import { getDbSafe } from './firebaseConfig';
import { doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { SafeStorage } from '../utils/safeStorage';

/**
 * What time of day this user actually uses the app, and where they are.
 *
 * YapStats Today is sent in each user's local evening, which needs two facts
 * the app has never recorded:
 *
 *   timezone        their IANA zone, so "8pm local" can be turned into a UTC
 *                   minute the scheduler can query on
 *   openHourCounts  a histogram of the local hours they open the app, so the
 *                   send can be anchored to when they are actually around
 *                   rather than to a fixed guess
 *
 * Neither can be backfilled — no history exists — so this starts recording
 * now and the anchor becomes usable as the histogram fills. Until then the
 * scheduler falls back to the 7-9pm window.
 *
 * Written at most once per local day per device. The heartbeat fires every 60
 * seconds, and a write on each of those would be thousands of pointless writes
 * per user per day to record a number that changes at most once an hour.
 */

const STAMP_KEY = 'yapstats_local_profile_day';

/** Minutes to ADD to UTC to get local time. Europe/Berlin in summer is +120. */
function utcOffsetMinutes(): number {
  // getTimezoneOffset is minutes to add to LOCAL to get UTC, i.e. the opposite
  // sign of how offsets are usually written. Negated here so the stored value
  // reads the way a human would say it.
  return -new Date().getTimezoneOffset();
}

function ianaTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Local calendar day, used to write at most once per day. */
function localDayKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Record this session's local hour and timezone, once per local day.
 *
 * Best-effort throughout: this is scheduling metadata for a recap
 * notification, and nothing about the app should fail because it could not be
 * written.
 */
export async function recordLocalTimeProfile(uid: string): Promise<void> {
  if (!uid) return;

  try {
    const today = localDayKey();
    if (SafeStorage.getItem(STAMP_KEY) === today) return;

    const hour = new Date().getHours(); // 0-23, local
    const tz = ianaTimeZone();

    const db = await getDbSafe();
    // updateDoc, never setDoc(..., { merge: true }). A merge-set on a document
    // this client has not loaded yet makes Firestore raise a local snapshot that
    // "exists" with only these four fields, and on a cold launch over a slow
    // network that stand-in reached UserDataContext before the real profile:
    // no username, no setupComplete, so the route guard sent onboarded users to
    // step 1 of setup. updateDoc requires the document to exist, so it can
    // neither fake one locally nor create a shell profile on the server for an
    // account whose document has not been written yet.
    await updateDoc(doc(db, 'users', uid), {
      timezone: tz || null,
      utcOffsetMinutes: utcOffsetMinutes(),
      // One counter per local hour. Incremented rather than overwritten so
      // the shape of someone's week survives a single unusual evening.
      [`openHourCounts.${hour}`]: increment(1),
      lastOpenHourRecordedAt: serverTimestamp(),
    });

    // Stamped only after the write lands, so a failure retries on the next
    // heartbeat rather than being skipped until tomorrow.
    SafeStorage.setItem(STAMP_KEY, today);
  } catch (error) {
    console.warn('[localTimeProfile] Could not record local time profile:', error);
  }
}
